import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const InstallPromptContext = createContext(null);

export const useInstallPrompt = () => useContext(InstallPromptContext);

// Detecta si la app ya corre en modo standalone (instalada). matchMedia es el
// estándar; navigator.standalone es el fallback propio de Safari/iOS, que no
// soporta ese media feature — se usa solo para este estado, nunca para el resto.
const detectStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallPromptProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone] = useState(detectStandalone);

  // Registrado en el Provider (raíz del árbol, ver App.js), nunca dentro de una
  // página: el evento se pierde para siempre si nadie escucha a tiempo, y las
  // páginas cargan en chunks lazy que pueden llegar tarde.
  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // El evento nativo solo puede usarse una vez; limpiarlo evita reintentos
    // con un objeto ya consumido.
    setDeferredPrompt(null);
    return choice;
  }, [deferredPrompt]);

  const value = {
    canInstall: Boolean(deferredPrompt),
    promptInstall,
    isStandalone,
  };

  return <InstallPromptContext.Provider value={value}>{children}</InstallPromptContext.Provider>;
};
